import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { useGridCopyMenu } from './gridCommon';
import { getMainAxios } from '../utils/util';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

interface DataItem {
  key: string;
  label: string;
  tableName: string;       // 표시용 (콤마 구분)
  relatedTables: string[]; // 관련 테이블 목록
  lastDate: string | null;
  lastCount: number;
  totalCount: number;
  status: 'OK' | 'DELAYED' | 'EMPTY' | 'ERROR' | 'UNKNOWN';
  delayDays: number | null;
  description: string;
  command: string | null;
  error: string | null;   // status=ERROR 일 때 실패 사유
}

interface StatusResponse {
  serverTime: string;
  today: string;
  lastBizday: string;
  items: DataItem[];
  errors?: string[];      // 일부 항목만 실패한 경우의 사유 목록
  bizdayMax?: string | null;  // 영업일 달력이 채워진 마지막 일자
  calStale?: boolean;         // 달력이 오늘보다 과거에서 끊겼는지
}

interface TableDailyCount {
  table: string;
  d: string;
  cnt: number;
}

interface DetailResponse {
  dailyCounts: TableDailyCount[];
  rows: any[];
}

const fmtDate = (s: string | null) => {
  if (!s) return '';
  return s.slice(0, 10);
};

/** axios 에러에서 화면에 표시할 실패 원인 추출 */
const describeAxiosError = (e: any): string => {
  // 1) 서버가 내려준 본문의 사유 (Results.Json / Results.Problem 모두 대응)
  const d = e?.response?.data;
  const serverMsg =
    typeof d === 'string' ? d
    : d?.error ?? d?.detail ?? d?.title ?? d?.message ?? null;

  const status = e?.response?.status;
  if (status) {
    const label = status === 401 ? '인증 만료(로그인 필요)'
      : status === 403 ? '권한 없음'
      : status === 404 ? '엔드포인트 없음'
      : status >= 500 ? '서버 오류'
      : '요청 오류';
    return serverMsg ? `HTTP ${status} ${label}: ${serverMsg}` : `HTTP ${status} ${label}`;
  }

  // 2) 응답 자체가 없는 경우 — 네트워크/타임아웃 구분
  if (e?.code === 'ECONNABORTED') return '요청 시간 초과 (서버 응답 지연)';
  if (e?.code === 'ERR_NETWORK') return '서버에 연결할 수 없음 (API 서버 중지 또는 네트워크 오류)';
  return e?.message || '알 수 없는 오류';
};

const statusCellRenderer = (params: { data: DataItem }) => {
  const { status, delayDays, error } = params.data;
  const styles: Record<string, React.CSSProperties> = {
    OK:      { background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7' },
    DELAYED: { background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' },
    EMPTY:   { background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' },
    ERROR:   { background: '#fecaca', color: '#7f1d1d', border: '1px solid #f87171' },
    UNKNOWN: { background: '#e5e7eb', color: '#374151', border: '1px solid #9ca3af' },
  };
  const labels: Record<string, string> = {
    OK: '✓ 정상',
    DELAYED: `⚠ ${delayDays}영업일 지연`,
    EMPTY: '✗ 데이터 없음',
    ERROR: '⛔ 조회 실패',
    UNKNOWN: '? 판정불가',
  };
  return (
    <span
      title={error ?? undefined}
      style={{
        ...styles[status],
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: '11px',
        fontWeight: 600,
        display: 'inline-block',
      }}
    >
      {labels[status]}
    </span>
  );
};

const runButtonRenderer = (
  params: { data: DataItem; context: { onRun: (key: string, label: string) => void; runningKey: string | null } }
) => {
  const { command, label } = params.data;
  if (!command) return null;
  const { onRun, runningKey } = params.context;
  const running = runningKey === command;
  return (
    <button
      onClick={() => onRun(command, label)}
      disabled={running}
      style={{
        padding: '2px 10px',
        background: running ? '#9ca3af' : '#3b82f6',
        color: 'white',
        border: 'none',
        borderRadius: 4,
        cursor: running ? 'not-allowed' : 'pointer',
        fontSize: '11px',
        fontWeight: 500,
      }}
    >
      {running ? '⏳ 실행중' : '▶ 실행'}
    </button>
  );
};

// 카테고리 탭 — item.key 기준으로 분류
type CatKey = 'all' | 'stk' | 'sfut' | 'ifut' | 'opt';

const CAT_TABS: { key: CatKey; label: string }[] = [
  { key: 'all',  label: '전체' },
  { key: 'stk',  label: '주식' },
  { key: 'sfut', label: '주식선물' },
  { key: 'ifut', label: '지수선물' },
  { key: 'opt',  label: '옵션(마스터)' },
];

const catOf = (key: string): CatKey =>
  key === 'sfut' ? 'sfut'
  : key === 'ifut' ? 'ifut'
  : key === 'opt' ? 'opt'
  : 'stk';

const DataCollectionMonitor: React.FC = () => {
  const axios = getMainAxios();
  const copyMenu = useGridCopyMenu();
  const [data, setData] = useState<StatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [runningKey, setRunningKey] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<DataItem | null>(null);
  const [detail, setDetail] = useState<DetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string>('');
  const [cat, setCat] = useState<CatKey>('all');

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get<StatusResponse>('/api/v1/admin/data-collection/status');
      setData(res.data);
      // 일부 항목만 실패한 경우 사유를 상단에 표시
      const errs = res.data.errors ?? [];
      setMessage(errs.length > 0
        ? `✗ 일부 항목 조회 실패 (${errs.length}건)\n${errs.join('\n')}`
        : '');
    } catch (e) {
      console.error('수집 현황 조회 실패:', e);
      setMessage(`✗ 수집 현황 조회 실패 — ${describeAxiosError(e)}`);
    } finally {
      setLoading(false);
    }
  }, [axios]);

  // race condition 방지: 마지막 요청만 반영
  const detailReqIdRef = React.useRef(0);

  const fetchDetail = useCallback(async (item: DataItem) => {
    const reqId = ++detailReqIdRef.current;
    setSelectedItem(item);
    setDetailLoading(true);
    setDetail(null);
    setDetailError('');
    try {
      // lastDate가 ISO ("2026-05-22T...") 또는 "2026-05-22" 둘 다 처리
      const dParam = item.lastDate ? item.lastDate.slice(0, 10) : undefined;
      const res = await axios.get<DetailResponse>('/api/v1/admin/data-collection/detail', {
        params: { key: item.key, d: dParam },
        timeout: 30_000,  // detail은 큰 테이블에서 수 초 걸릴 수 있음
      });
      if (reqId === detailReqIdRef.current) setDetail(res.data);
    } catch (e) {
      console.error('상세 조회 실패:', e);
      if (reqId === detailReqIdRef.current) {
        setDetail({ dailyCounts: [], rows: [] });
        setDetailError(describeAxiosError(e));
      }
    } finally {
      if (reqId === detailReqIdRef.current) setDetailLoading(false);
    }
  }, [axios]);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 60_000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const handleRun = useCallback(async (key: string, label: string) => {
    if (!confirm(`"${label}" 수집을 지금 실행하시겠습니까?\n(백그라운드에서 실행되며 완료까지 시간이 걸립니다)`)) return;
    setRunningKey(key);
    setMessage('');
    try {
      const res = await axios.post<{ success: boolean; message: string }>(
        `/api/v1/admin/data-collection/run/${key}`
      );
      setMessage(`✓ ${res.data.message}`);
      setTimeout(fetchStatus, 2000);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || '실행 실패';
      setMessage(`✗ ${msg}`);
    } finally {
      setRunningKey(null);
    }
  }, [axios, fetchStatus]);

  const summaryColDefs = useMemo<ColDef<DataItem>[]>(() => [
    { headerName: '항목', field: 'label', flex: 1.5, minWidth: 180 },
    { headerName: '테이블', field: 'tableName', flex: 1.5, minWidth: 200,
      cellStyle: { fontFamily: 'monospace', fontSize: '11px' },
    },
    {
      headerName: '상태', field: 'status', width: 140,
      cellRenderer: statusCellRenderer,
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    { headerName: '마지막 일자', field: 'lastDate', width: 120,
      valueFormatter: (p) => fmtDate(p.value),
      cellStyle: { textAlign: 'center' },
    },
    { headerName: '해당일 row', field: 'lastCount', width: 120,
      valueFormatter: (p) => (p.value ?? 0).toLocaleString(),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' },
    },
    {
      headerName: '실패 원인', field: 'error', flex: 2, minWidth: 240,
      valueGetter: (p) =>
        p.data?.error
        ?? (p.data?.status === 'UNKNOWN'
            ? '영업일 달력 미등록 구간 — 지연 여부 판정 불가'
            : ''),
      tooltipValueGetter: (p: any) => p.value,
      cellStyle: { color: '#991b1b', fontSize: '11px' },
    },
    { headerName: '수집 방법', field: 'description', flex: 2, minWidth: 280 },
    {
      headerName: '수동 실행', field: 'command', width: 110,
      cellRenderer: runButtonRenderer,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
    },
  ], []);

  // 최근 5일 카운트를 테이블별 행으로 표시 (피벗: 행=테이블, 컬럼=일자)
  const dailyCountPivot = useMemo(() => {
    if (!detail) return { rows: [], dates: [] as string[] };
    const dates = Array.from(new Set(detail.dailyCounts.map(c => c.d.slice(0, 10)))).sort().reverse();
    const tables = Array.from(new Set(detail.dailyCounts.map(c => c.table)));
    const rows = tables.map(t => {
      const row: any = { table: t };
      for (const d of dates) {
        const m = detail.dailyCounts.find(c => c.table === t && c.d.slice(0, 10) === d);
        row[d] = m?.cnt ?? 0;
      }
      return row;
    });
    return { rows, dates };
  }, [detail]);

  const dailyCountColDefs = useMemo<ColDef[]>(() => {
    const cols: ColDef[] = [
      { headerName: '테이블', field: 'table', width: 180,
        cellStyle: { fontFamily: 'monospace', fontSize: '12px', fontWeight: 600 } },
    ];
    for (const d of dailyCountPivot.dates) {
      cols.push({
        headerName: d.slice(5), // MM-DD
        field: d,
        flex: 1,
        minWidth: 90,
        valueFormatter: (p: any) => (p.value ?? 0).toLocaleString(),
        cellStyle: { textAlign: 'right', fontFamily: 'monospace' },
      });
    }
    return cols;
  }, [dailyCountPivot.dates]);

  const detailColDefs = useMemo<ColDef[]>(() => {
    if (!detail || detail.rows.length === 0) return [];
    return Object.keys(detail.rows[0]).map((k) => ({
      headerName: k,
      field: k,
      flex: 1,
      minWidth: 100,
      valueFormatter: (p: any) => {
        const v = p.value;
        if (v == null) return '';
        if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 19).replace('T', ' ');
        if (typeof v === 'number') return v.toLocaleString();
        return String(v);
      },
    }));
  }, [detail]);

  // 카테고리 필터 적용 항목
  const filteredItems = useMemo(
    () => (data?.items ?? []).filter(i => cat === 'all' || catOf(i.key) === cat),
    [data, cat]
  );

  // 첫 항목 자동 선택
  useEffect(() => {
    if (data && data.items.length > 0 && selectedItem === null) {
      fetchDetail(data.items[0]);
    }
  }, [data, selectedItem, fetchDetail]);

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>

      {/* 상단 헤더 카드 */}
      <div style={{
        backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px',
        padding: '16px', marginBottom: '20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, color: '#1e40af', fontSize: '14px', fontWeight: 600 }}>
            데이터 수집 현황
          </h3>
          <button
            onClick={fetchStatus}
            disabled={loading}
            style={{
              padding: '4px 12px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'wait' : 'pointer',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            {loading ? '⏳ 조회 중...' : '🔄 새로고침'}
          </button>
          {data && (
            <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#6b7280' }}>
              서버시각: {new Date(data.serverTime).toLocaleString('ko-KR')} ·
              오늘: {fmtDate(data.today)} ·
              <span style={data.calStale ? { color: '#b91c1c', fontWeight: 700 } : undefined}>
                최근 영업일: {fmtDate(data.lastBizday)}
                {data.calStale && ` (달력 ${fmtDate(data.bizdayMax ?? null)} 까지만 등록됨)`}
              </span>
            </span>
          )}
        </div>

        {message && (
          <div style={{
            marginTop: '12px',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            background: message.startsWith('✓') ? '#d1fae5' : '#fee2e2',
            color:      message.startsWith('✓') ? '#065f46' : '#991b1b',
            border: message.startsWith('✓') ? '1px solid #6ee7b7' : '1px solid #fca5a5',
            whiteSpace: 'pre-wrap',   // 여러 항목 실패 사유를 줄바꿈으로 표시
          }}>
            {message}
          </div>
        )}
      </div>

      {/* 수집 현황 그리드 */}
      <div style={{
        backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px',
        padding: '16px', marginBottom: '20px',
      }}>
        {/* 카테고리 탭 */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
          {CAT_TABS.map(t => {
            const cnt = t.key === 'all'
              ? (data?.items.length ?? 0)
              : (data?.items.filter(i => catOf(i.key) === t.key).length ?? 0);
            const active = cat === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setCat(t.key)}
                style={{
                  padding: '4px 12px',
                  fontSize: '12px',
                  fontWeight: active ? 700 : 500,
                  color: active ? 'white' : '#1e40af',
                  background: active ? '#3b82f6' : 'white',
                  border: '1px solid #3b82f6',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                {t.label} ({cnt})
              </button>
            );
          })}
        </div>

        <div style={{ marginBottom: '8px', fontSize: '12px', color: '#1e40af', fontWeight: 600 }}>
          {CAT_TABS.find(t => t.key === cat)?.label} 항목 ({filteredItems.length}건) — 행 클릭 시 하단에 상세 표시
        </div>
        <div className="ag-theme-quartz" style={{ height: 400, width: '100%', fontSize: '12px' }}>
          <AgGridReact<DataItem>
            rowData={filteredItems}
            columnDefs={summaryColDefs}
            defaultColDef={{ sortable: true, filter: true, resizable: true }}
            context={{ onRun: handleRun, runningKey }}
            onRowClicked={(e) => { if (e.data) fetchDetail(e.data); }}
            getRowStyle={(p) =>
              p.data?.key === selectedItem?.key
                ? { backgroundColor: '#dbeafe' }
                : undefined
            }
            rowHeight={32}
            headerHeight={32}
            {...copyMenu.gridProps}
          />
        </div>
      </div>

      {/* 관련 테이블별 최근 5일 카운트 */}
      {selectedItem && (
        <div style={{
          backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px',
          padding: '16px', marginBottom: '20px',
        }}>
          <div style={{ marginBottom: '8px', fontSize: '12px', color: '#1e40af', fontWeight: 600 }}>
            관련 테이블 최근 5영업일 적재 건수
            <span style={{ marginLeft: 8, fontWeight: 400, color: '#6b7280' }}>
              — {selectedItem.label}
            </span>
          </div>
          <div className="ag-theme-quartz"
               style={{ height: Math.max(120, (dailyCountPivot.rows.length + 1) * 32 + 8), width: '100%', fontSize: '12px' }}>
            <AgGridReact
              rowData={dailyCountPivot.rows}
              columnDefs={dailyCountColDefs}
              defaultColDef={{ sortable: false, filter: false, resizable: true }}
              rowHeight={28}
              headerHeight={32}
              {...copyMenu.gridProps}
            />
          </div>
        </div>
      )}

      {/* 대표 테이블 상세 그리드 */}
      <div style={{
        backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px',
        padding: '16px', marginBottom: '20px',
      }}>
        <div style={{ marginBottom: '8px', fontSize: '12px', color: '#1e40af', fontWeight: 600 }}>
          상세 내역
          {selectedItem && (
            <span style={{ marginLeft: 8, fontWeight: 400, color: '#6b7280' }}>
              — {selectedItem.relatedTables[0]} · {fmtDate(selectedItem.lastDate)} · {detail?.rows.length ?? 0}건 (최대 500)
            </span>
          )}
        </div>
        <div className="ag-theme-quartz" style={{ height: 500, width: '100%', fontSize: '12px' }}>
          {detailLoading ? (
            <div style={{ padding: '20px', color: '#1e40af', fontSize: '12px', fontWeight: 500 }}>
              데이터 로드 중...
            </div>
          ) : detailError ? (
            <div style={{
              padding: '12px', margin: '0 0 8px', borderRadius: 4, fontSize: '12px',
              background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5',
              whiteSpace: 'pre-wrap',
            }}>
              ✗ 상세 조회 실패 — {detailError}
            </div>
          ) : (
            <AgGridReact
              rowData={detail?.rows ?? []}
              columnDefs={detailColDefs}
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
              rowHeight={28}
              headerHeight={32}
              {...copyMenu.gridProps}
            />
          )}
        </div>
      </div>

      {/* 우클릭 컨텍스트 메뉴 (셀/행 복사) */}
      {copyMenu.menu}
    </div>
  );
};

export default DataCollectionMonitor;
